import { useEffect, useState } from 'react';
import { Image, ImageSourcePropType, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActionHeader } from '../components/AppHeader';
import BottomTabBar from '../components/BottomTabBar';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, getAudiobookBySlug } from '../api/audiobooks';
import { htmlToPlainText } from '../utils/html';
import { useFavoriteState } from '../hooks/useFavoriteState';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'BookReviews'>;

const coverImage = require('../../assets/aural-continue-thumb.png');

function imageSource(url: string | null | undefined): ImageSourcePropType {
  return url ? { uri: url } : coverImage;
}

function authorName(book: Audiobook) {
  return narratorName(book);
}

function DetailSkeleton() {
  return (
    <>
      <View style={styles.hero}>
        <View style={[styles.cover, styles.skeletonBlock]} />
        <View style={styles.heroDetails}>
          <View style={[styles.skeletonBlock, styles.skeletonTitle]} />
          <View style={[styles.skeletonBlock, styles.skeletonLineSmall]} />
          <View style={[styles.skeletonBlock, styles.skeletonLineMedium]} />
        </View>
      </View>
      <View style={[styles.skeletonBlock, styles.skeletonReviewSummary]} />
      <View style={[styles.skeletonBlock, styles.skeletonReviewCard]} />
    </>
  );
}

export default function BookReviewsScreen({ navigation, route }: Props) {
  const [book, setBook] = useState<Audiobook | null>(null);
  const params = route.params;

  useEffect(() => {
    let mounted = true;
    setBook(null);
    if (params?.slug) {
      getAudiobookBySlug(params.slug).then((data) => mounted && setBook(data)).catch(() => mounted && setBook(null));
    }
    return () => {
      mounted = false;
    };
  }, [params?.slug]);

  const detailParams = { id: book?.id ?? params?.id, slug: book?.slug ?? params?.slug };
  const favoriteState = useFavoriteState(book?.id ?? params?.id, () => navigation.navigate('Login'));

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <ActionHeader
          onBack={() => navigation.goBack()}
          rightIcons={[favoriteState.favorite ? 'heart' : 'heart-outline', 'ellipsis-horizontal']}
          onRightPress={[favoriteState.toggle]}
        />

        {!book ? (
          <DetailSkeleton />
        ) : (
          <>
            <View style={styles.hero}>
              <Image source={imageSource(book.cover)} style={styles.cover} resizeMode="cover" />
              <View style={styles.heroDetails}>
                <Text style={styles.title}>{book.name}</Text>
                <Text style={styles.author}>{authorName(book)}</Text>
                <View style={styles.ratingRow}>
                  <Text style={styles.stars}>{'\u2605\u2605\u2605\u2605\u2605'}</Text>
                  <Text style={styles.rating}>{book.rating_average ?? 0} / 5.0 ({book.rating_count ?? 0})</Text>
                </View>
              </View>
            </View>

            <View style={styles.buttons}>
              <Pressable style={styles.primaryButton} onPress={() => navigation.navigate('Player', detailParams)}>
                <Ionicons name="play" size={20} color={colors.buttonText} />
                <Text style={styles.primaryText}>Nghe ngay</Text>
              </Pressable>
            </View>

            <View style={styles.tabs}>
              <Pressable style={styles.tab} onPress={() => navigation.navigate('BookOverview', detailParams)}><Text style={styles.tabText}>{'T\u1ed5ng quan'}</Text></Pressable>
              <Pressable style={styles.tab} onPress={() => navigation.navigate('BookInformation', detailParams)}><Text style={styles.tabText}>{'Th\u00f4ng tin'}</Text></Pressable>
              <Pressable style={[styles.tab, styles.tabActive]}><Text style={[styles.tabText, styles.tabTextActive]}>{'\u0110\u00e1nh gi\u00e1'}</Text></Pressable>
            </View>

            <View style={styles.reviewSummary}>
              <Text style={styles.bigRating}>{book.rating_average ?? 0}/5.0</Text>
              <Text style={styles.reviewCount}>{book.rating_count ?? 0} {'\u0111\u00e1nh gi\u00e1'}</Text>
            </View>

            <View style={styles.reviewCard}>
              <Text style={styles.reviewTitle}>{'N\u1ed9i dung \u0111\u01b0\u1ee3c y\u00eau th\u00edch'}</Text>
              <Text style={styles.reviewMeta}>{'T\u1ed5ng h\u1ee3p t\u1eeb h\u1ec7 th\u1ed1ng'}</Text>
              <Text style={styles.reviewText}>{htmlToPlainText(book.ai_summary ?? book.description)}</Text>
            </View>
          </>
        )}
      </ScrollView>
      <BottomTabBar active="Home" onHome={() => navigation.navigate('Home')} onDiscover={() => navigation.navigate('GetSuggestions')} onLibrary={() => navigation.navigate('Library')} onProfile={() => navigation.navigate('Settings')} />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: 'rgb(242, 249, 253)' },
  container: { paddingHorizontal: 24, paddingTop: 62, paddingBottom: 124 },
  hero: { flexDirection: 'row', alignItems: 'center', gap: 24, marginBottom: 32 },
  cover: { width: 100, height: 100, borderRadius: 8, backgroundColor: colors.card },
  heroDetails: { width: 197, gap: 8 },
  title: { color: colors.textPrimary, fontSize: 20, fontWeight: '600' },
  author: { color: colors.textSecondary, fontSize: 14 },
  ratingRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  stars: { color: '#FFB200', fontSize: 14, letterSpacing: 0 },
  rating: { color: colors.textPrimary, fontSize: 14 },
  buttons: { flexDirection: 'row', gap: 16, marginBottom: 24 },
  primaryButton: { flex: 1, height: 48, borderRadius: 8, backgroundColor: colors.button, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  secondaryButton: { flex: 1, height: 48, borderRadius: 8, borderWidth: 1, borderColor: colors.textPrimary, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  primaryText: { color: colors.buttonText, fontSize: 14, fontWeight: '600' },
  secondaryText: { color: colors.textPrimary, fontSize: 14, fontWeight: '600' },
  tabs: { flexDirection: 'row', marginBottom: 24 },
  tab: { width: 109, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.border, alignItems: 'center' },
  tabActive: { borderBottomColor: colors.textPrimary },
  tabText: { color: colors.textSecondary, fontSize: 14 },
  tabTextActive: { color: colors.textPrimary, fontWeight: '500' },
  reviewSummary: { marginBottom: 16 },
  bigRating: { color: colors.textPrimary, fontSize: 20, fontWeight: '600', marginBottom: 4 },
  reviewCount: { color: colors.textSecondary, fontSize: 14, marginBottom: 12 },
  writeButton: { height: 44, borderRadius: 8, backgroundColor: colors.card, alignItems: 'center', justifyContent: 'center' },
  writeText: { color: colors.button, fontSize: 14, fontWeight: '600' },
  reviewCard: { backgroundColor: colors.card, borderRadius: 12, padding: 16, marginBottom: 16 },
  reviewTitle: { color: colors.textPrimary, fontSize: 16, fontWeight: '600', marginBottom: 8 },
  reviewMeta: { color: colors.textSecondary, fontSize: 12, marginBottom: 12 },
  reviewText: { color: colors.textSecondary, fontSize: 14, lineHeight: 21 },
  skeletonBlock: { backgroundColor: '#ECEEF2' },
  skeletonTitle: { width: 176, height: 22, borderRadius: 6 },
  skeletonLineSmall: { width: 96, height: 14, borderRadius: 6 },
  skeletonLineMedium: { width: 132, height: 14, borderRadius: 6 },
  skeletonReviewSummary: { height: 84, borderRadius: 8, marginBottom: 16 },
  skeletonReviewCard: { height: 148, borderRadius: 12 },
});
